import { track } from "~/modules/analytics/track";
import { DownloadsUserEvents } from "../analytics/events/downloads";
import { ExportTableName, ExportTypes } from "./types";

// // // //

/**
 * trackLoginCtaConfirmed
 * Tracks the event when a user confirms the login call-to-action.
 */
export function trackLoginCtaConfirmed() {
    track(DownloadsUserEvents.LoginCtaConfirmed);
}

/**
 * trackLoginCtaClosed
 * Tracks the event when a user closes the login call-to-action.
 */
export function trackLoginCtaClosed() {
    track(DownloadsUserEvents.LoginCtaClosed);
}

// // // //
// Analytics for data exports

/**
 * trackCustomExcelExportError
 * Tracks the event when a user fails to successfully download an Excel export.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomExcelExportError(props: {
    source: ExportTableName;
}) {
    track(DownloadsUserEvents.CustomExcelExportError, props);
}

/**
 * trackCustomExcelExportSuccess
 * Tracks the event when a user successfully downloads an Excel export.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomExcelExportSuccess(props: {
    source: ExportTableName;
}) {
    track(DownloadsUserEvents.CustomExcelExportSuccess, props);
}

/**
 * trackCustomExcelExportAttempted
 * Tracks the event when a user attempts to download an Excel export but is not logged in.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomExcelExportAttempted(props: {
    source: ExportTableName;
}) {
    track(DownloadsUserEvents.CustomExcelExportAttempted, props);
}

/**
 * trackCustomCsvExportError
 * Tracks the event when a user fails to successfully download a CSV export.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomCsvExportError(props: { source: ExportTableName }) {
    track(DownloadsUserEvents.CustomCsvExportError, props);
}

/**
 * trackCustomCsvExportSuccess
 * Tracks the event when a user successfully downloads a CSV export.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomCsvExportSuccess(props: {
    source: ExportTableName;
}) {
    track(DownloadsUserEvents.CustomCsvExportSuccess, props);
}

/**
 * trackCustomCsvExportAttempted
 * Tracks the event when a user attempts to download a CSV export but is not logged in.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomCsvExportAttempted(props: {
    source: ExportTableName;
}) {
    track(DownloadsUserEvents.CustomCsvExportAttempted, props);
}

/**
 * trackCustomCsvExportConfirmed
 * Tracks the event when a user confirms the download of a CSV export.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomCsvExportConfirmed(props: {
    source: ExportTableName;
}) {
    track(DownloadsUserEvents.CustomCsvExportConfirmed, props);
}

/**
 * trackCustomExcelExportConfirmed
 * Tracks the event when a user confirms the download of an Excel export.
 * @param {ExportTableName} props.source - The source of the export.
 */
export function trackCustomExcelExportConfirmed(props: {
    source: ExportTableName;
}) {
    track(DownloadsUserEvents.CustomExcelExportConfirmed, props);
}

// // // //
// Analytics for DataExportProvider
// NOTE - replace the above with these functions once we've released the new export provider

export function trackExportAttempted({
    type,
    source,
}: {
    type: ExportTypes;
    source: ExportTableName;
}) {
    if (type === ExportTypes.csv) {
        trackCustomCsvExportAttempted({ source });
    } else if (type === ExportTypes.excel) {
        trackCustomExcelExportAttempted({ source });
    }
}

export function trackExportSuccess({
    type,
    source,
}: {
    type: ExportTypes;
    source: ExportTableName;
}) {
    if (type === ExportTypes.csv) {
        trackCustomCsvExportSuccess({ source });
    } else if (type === ExportTypes.excel) {
        trackCustomExcelExportSuccess({ source });
    }
}

export function trackExportError({
    type,
    source,
}: {
    type: ExportTypes;
    source: ExportTableName;
}) {
    if (type === ExportTypes.csv) {
        trackCustomCsvExportError({ source });
    } else if (type === ExportTypes.excel) {
        trackCustomExcelExportError({ source });
    }
}

export function trackExportConfirmed({
    type,
    source,
}: {
    type: ExportTypes;
    source: ExportTableName;
}) {
    if (type === ExportTypes.csv) {
        trackCustomCsvExportConfirmed({ source });
    } else if (type === ExportTypes.excel) {
        trackCustomExcelExportConfirmed({ source });
    }
}
